import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Currency from 'src/app/components/Currency';
import FeedbackDrawer from 'src/app/components/FeedbackDrawer';
import { LinkPagamento } from 'src/models/LinkPagamento';
import { TipoPagamento } from 'src/models/TipoPagamento';
import { Routes } from 'src/routes/index.routes';
import LinkPagamentoService from 'src/services/LinkPagamento.service';
import { Colors } from 'src/shared/colors';

import Error from '../../../assets/associar-error.svg';
import Sucess from '../../../assets/sucesso-pagamento.svg';
import { setUsarYuno, setValor } from '../../../shared/store/Pagamento/payment.slice';
import { Pagamento } from '../adicionar-saldo/Pagamento/Pagamento';

import { ConfirmacaoPosPago } from './ConfirmacaoPosPago';
import { InformacoesBilhete } from './InformacoesBilhete';

const CargaDistancia = () => {
  const { id } = useParams<{ id: string }>();
  const [pagamento, setPagamento] = useState<LinkPagamento>();
  const [errorMsg, setErrorMsg] = useState('');
  const [completed, setCompleted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!id) return;

    // Busca o pagamento
    LinkPagamentoService.buscar(id).then((pagamento) => {
      setPagamento(pagamento);
      setCompleted(pagamento.pagamentoAutorizado);
      dispatch(setValor(pagamento.valor));

      // Se o backend indicou que é Yuno, ativa a flag na store para que
      // o componente <Pagamento> saiba qual SDK renderizar.
      if (
        pagamento.tipoPagamento === TipoPagamento.YunoCredito ||
        pagamento.tipoPagamento === TipoPagamento.YunoDebito ||
        pagamento.tipoPagamento === TipoPagamento.YunoPix
      ) {
        dispatch(setUsarYuno(true));
      } else {
        dispatch(setUsarYuno(false));
      }
    });
  }, [id, dispatch]);

  useEffect(() => {
    // Se não houver pagamento ou se `linkValidoAte` estiver ausente, não configura o temporizador
    if (!pagamento?.linkValidoAte) return;

    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const expiry = new Date(pagamento.linkValidoAte).getTime();
      const expiryWithExtraTime = expiry + 3 * 60 * 60 * 1000; // Adiciona 3 horas em milissegundos
      const timeDiff = expiryWithExtraTime - now;

      // Calcule o tempo restante
      const minutes = new Date(timeDiff).getMinutes();
      const seconds = new Date(timeDiff).getSeconds();

      // Formata para exibição
      setTimeRemaining(`${minutes}m ${seconds.toString().padStart(2, '0')}s restantes`);
    };

    // Chama a função de cálculo imediatamente e, em seguida
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval); // Limpeza do intervalo ao desmontar o componente
  }, [pagamento]);

  if (!pagamento || !id) return null;

  const compraPassagem = pagamento.bilhetes && pagamento.bilhetes.length > 0;

  const navigateToHome = () => {
    navigate(Routes.Home);
  };

  if (completed)
    return (
      <Box width={{ xs: '100%', md: '40%' }} display="flex" height={0.9} margin="auto">
        <FeedbackDrawer
          titulo={compraPassagem ? 'Compra concluída!' : 'Carga aprovada!'}
          subtitulo={
            compraPassagem ? 'Compra concluída com sucesso!' : 'Carga aprovada com sucesso!'
          }
          textoBotao="Fechar"
          imagemModal={Sucess}
          onClick={navigateToHome}
        />
      </Box>
    );

  if (errorMsg)
    return (
      <Box width={{ xs: '100%', md: '40%' }} display="flex" height={0.9} margin="auto">
        <FeedbackDrawer
          titulo={compraPassagem ? 'Erro ao concluir a compra' : 'Erro ao realizar carga!'}
          subtitulo={errorMsg}
          textoBotao="Fechar"
          imagemModal={Error}
          onClick={navigateToHome}
        />
      </Box>
    );

  if (pagamento.solicitacaoExpirada)
    return (
      <Box p={{ xs: 2, sm: 4 }}>
        <Box my={4}>
          <Typography textAlign="center" variant="h4" color={Colors.primary.main} my={2}>
            Link expirado!
          </Typography>

          <Typography textAlign="center" mt={4}>
            A solicitação de pagamento pelo link atual expirou.
          </Typography>
          <Typography textAlign="center" mb={2}>
            Por favor, solicite um novo link de pagamento
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center">
          <Box mt={8} width={{ xs: '100%', sm: '50%' }}>
            <img width="100%" src={Error} alt="pagamento_realizado" />
          </Box>
        </Box>
      </Box>
    );

  return (
    <Box p={{ xs: 2, sm: 4 }}>
      <Box my={4}>
        <Typography textAlign="center" variant="h4" color={Colors.primary.main} my={2}>
          {compraPassagem ? 'Compra de passagem' : 'Solicitação de carga'}
        </Typography>

        <Typography textAlign="center" color="secondary" mt={2}>
          Duração do link de pagamento: {timeRemaining}
        </Typography>

        <Typography textAlign="center" mt={2}>
          Existe
          {compraPassagem ? ' uma compra de passagem ' : ' uma solicitação de carga '}
          em aberto!
        </Typography>

        <Typography textAlign="center" mb={2}>
          Prossiga com o pagamento caso as informações estejam corretas.
        </Typography>
      </Box>

      <Box display="flex" justifyContent="center">
        <Box width={{ xs: '100%', md: '60%' }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
          >
            <Typography display="inline" color={Colors.secondary}>
              Valor da {compraPassagem ? ' compra' : ' carga'}
            </Typography>
            <Typography display="inline" variant="h6" color={Colors.primary.dark}>
              <Currency value={pagamento.valor} />
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
          >
            <Typography display="inline" color={Colors.secondary}>
              Usuário
            </Typography>
            <Typography display="inline" variant="h6" color={Colors.primary.dark}>
              {pagamento.usuarioNome}
            </Typography>
          </Box>

          {pagamento.usuarioSetor && (
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
            >
              <Typography display="inline" color={Colors.secondary}>
                Setor:
              </Typography>
              <Typography display="inline" variant="h6" color={Colors.primary.dark}>
                {pagamento.usuarioSetor}
              </Typography>
            </Box>
          )}

          {compraPassagem && (
            <Box display="flex" flexDirection="column">
              <Typography my={1} variant="h6" color={Colors.primary.main}>
                Bilhetes
              </Typography>
              {pagamento.bilhetes &&
                pagamento.bilhetes.map((bilhete) => (
                  <InformacoesBilhete
                    bilhete={bilhete}
                    key={`${bilhete.dataEmbarque}-${bilhete.linha}-${bilhete.poltrona}`}
                  />
                ))}
            </Box>
          )}

          <Box my={4}>
            {pagamento.tipoPagamento === TipoPagamento.PJPosPago || compraPassagem ? (
              <ConfirmacaoPosPago
                idSolicitacaoLink={id}
                onError={setErrorMsg}
                onSucess={() => setCompleted(true)}
              />
            ) : (
              <Pagamento
                pagamentoId={id}
                pagamentoValor={pagamento.valor}
                tipoPagamento={pagamento.tipoPagamento}
                pagamentoCpfCnpj={pagamento.cpfCnpj}
                onPaymentCompleted={() => setCompleted(true)}
                onPaymentFailed={(message?: string) =>
                  setErrorMsg(message ?? 'O pagamento nao foi aprovado')
                }
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CargaDistancia;
